/**
 * Structured per-eye refraction (`BACKLOG.md` M10) — sphere/cylinder/axis/add/prism per eye, with the
 * exam method distinguished (manifest/autorefraction/cycloplegic/subjective-final). Pure vocabulary and
 * cross-field validation only; storage is `db/schema/ophthalmology.ts`'s `ophthalmologyRefractions`.
 */

export const REFRACTION_TYPES = [
  'manifest',
  'autorefraction',
  'cycloplegic',
  'subjective_final',
] as const;
export type RefractionType = (typeof REFRACTION_TYPES)[number];

export function isRefractionType(value: string): value is RefractionType {
  return (REFRACTION_TYPES as readonly string[]).includes(value);
}

const REFRACTION_TYPE_LABELS: Readonly<Record<RefractionType, string>> = {
  manifest: 'Manifest refraction',
  autorefraction: 'Autorefraction',
  cycloplegic: 'Cycloplegic refraction',
  subjective_final: 'Subjective final refraction',
};

export function refractionTypeLabel(type: RefractionType): string {
  return REFRACTION_TYPE_LABELS[type];
}

export const PRISM_BASES = ['up', 'down', 'in', 'out'] as const;
export type PrismBase = (typeof PRISM_BASES)[number];

export function isPrismBase(value: string): value is PrismBase {
  return (PRISM_BASES as readonly string[]).includes(value);
}

export const MIN_AXIS_DEGREES = 0;
export const MAX_AXIS_DEGREES = 180;

/**
 * A per-eye refraction reading, as it appears on both `ophthalmologyRefractions` and
 * `ophthalmologyEyewearPrescriptions` (one eye's worth of the shared `od*`/`os*` column shape).
 */
export interface EyeRefractionReading {
  readonly sphere: number | null;
  readonly cylinder: number | null;
  readonly axisDegrees: number | null;
  readonly add: number | null;
}

/**
 * Cross-field consistency for one eye's reading: an axis only means something alongside a non-null
 * cylinder (a pure sphere has no meaningful axis), and a cylinder without an axis is incomplete data,
 * not a valid reading. Returns `true` when consistent — the request schema's own `.superRefine` is
 * where an inconsistency actually becomes a rejected request; this function is the rule, not the
 * enforcement point, matching `orthopedics/examManeuvers.ts`'s `maneuverSupportsGrade` precedent for
 * keeping a pure predicate importable from both the request schema and any future caller.
 */
export function isConsistentEyeRefractionReading(reading: EyeRefractionReading): boolean {
  const hasCylinder = reading.cylinder !== null;
  const hasAxis = reading.axisDegrees !== null;
  return hasCylinder === hasAxis;
}
